import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users,
  FileText,
  Briefcase,
  BookOpen,
  FolderOpen,
  Eye,
} from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { PageLoader } from '@/components/ui/Spinner';
import { useAuth } from '@/features/auth';
import { useLeadStats } from '@/hooks/useLeads';
import { useBlogPosts } from '@/hooks/useBlog';
import { useServices } from '@/hooks/useServices';
import { useCaseStudies } from '@/hooks/useCaseStudies';
import { useTranslation } from '@/features/i18n';

export function DashboardPage() {
  const { profile } = useAuth();
  const { data: leadStats, isLoading: leadsLoading } = useLeadStats();
  const { data: blogData, isLoading: blogLoading } = useBlogPosts({ includeUnpublished: true, limit: 5 });
  const { data: services, isLoading: servicesLoading } = useServices({ includeUnpublished: true });
  const { data: caseStudies, isLoading: casesLoading } = useCaseStudies({ includeUnpublished: true });
  const { t } = useTranslation();

  const isLoading = leadsLoading || blogLoading || servicesLoading || casesLoading;

  if (isLoading) {
    return <PageLoader />;
  }

  const stats = [
    {
      title: t.admin.dashboard.newLeads,
      value: leadStats?.new || 0,
      total: leadStats?.total || 0,
      icon: Users,
      color: 'bg-blue-500',
      href: '/admin/leads',
    },
    {
      title: t.admin.dashboard.blogPosts,
      value: blogData?.posts.filter(p => p.status === 'published').length || 0,
      total: blogData?.total || 0,
      icon: BookOpen,
      color: 'bg-green-500',
      href: '/admin/blog',
    },
    {
      title: t.admin.dashboard.services,
      value: services?.filter(s => s.status === 'published').length || 0,
      total: services?.length || 0,
      icon: Briefcase,
      color: 'bg-purple-500',
      href: '/admin/services',
    },
    {
      title: t.admin.dashboard.caseStudies,
      value: caseStudies?.filter(c => c.status === 'published').length || 0,
      total: caseStudies?.length || 0,
      icon: FolderOpen,
      color: 'bg-orange-500',
      href: '/admin/case-studies',
    },
  ];

  const quickActions = [
    { label: t.admin.dashboard.newBlogPost, href: '/admin/blog', icon: BookOpen },
    { label: t.admin.dashboard.viewLeads, href: '/admin/leads', icon: Users },
    { label: t.admin.dashboard.editServices, href: '/admin/services', icon: Briefcase },
    { label: t.admin.settings.title, href: '/admin/settings', icon: FileText },
  ];

  return (
    <>
      <SEO title={t.admin.dashboard.title} noIndex />

      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            {t.admin.dashboard.welcome}, {profile?.full_name || 'Admin'}
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            {t.admin.dashboard.overview}
          </p>
        </div>

        {/* Stats grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Link to={stat.href}>
                <Card hover className="relative overflow-hidden">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {stat.title}
                      </p>
                      <p className="text-3xl font-bold text-neutral-900 dark:text-white mt-1">
                        {stat.value}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1">
                        {t.admin.dashboard.ofTotal.replace('{total}', String(stat.total))}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Recent leads */}
          <div className="lg:col-span-2">
            <Card>
              <div className="flex items-center justify-between mb-6">
                <CardTitle>{t.admin.dashboard.leadStatusOverview}</CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/admin/leads">{t.admin.dashboard.viewAll}</Link>
                </Button>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {leadStats?.new || 0}
                  </div>
                  <div className="text-sm text-blue-600 dark:text-blue-400">{t.admin.leads.status.new}</div>
                </div>
                <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    {leadStats?.contacted || 0}
                  </div>
                  <div className="text-sm text-yellow-600 dark:text-yellow-400">{t.admin.leads.status.contacted}</div>
                </div>
                <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {leadStats?.qualified || 0}
                  </div>
                  <div className="text-sm text-purple-600 dark:text-purple-400">{t.admin.leads.status.qualified}</div>
                </div>
                <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {leadStats?.proposal || 0}
                  </div>
                  <div className="text-sm text-orange-600 dark:text-orange-400">{t.admin.dashboard.proposalSent}</div>
                </div>
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {leadStats?.won || 0}
                  </div>
                  <div className="text-sm text-green-600 dark:text-green-400">{t.admin.leads.status.won}</div>
                </div>
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20">
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {leadStats?.lost || 0}
                  </div>
                  <div className="text-sm text-red-600 dark:text-red-400">{t.admin.leads.status.lost}</div>
                </div>
              </div>
            </Card>
          </div>

          {/* Quick actions */}
          <div>
            <Card>
              <CardTitle className="mb-6">{t.admin.dashboard.quickActions}</CardTitle>
              <div className="space-y-3">
                {quickActions.map((action) => (
                  <Link
                    key={action.label}
                    to={action.href}
                    className="flex items-center p-3 rounded-lg bg-neutral-50 dark:bg-neutral-700/50 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                  >
                    <action.icon className="w-5 h-5 text-neutral-600 dark:text-neutral-400 mr-3" />
                    <span className="text-neutral-900 dark:text-white font-medium">
                      {action.label}
                    </span>
                  </Link>
                ))}
              </div>
            </Card>
          </div>
        </div>

        {/* Recent blog posts */}
        {blogData?.posts && blogData.posts.length > 0 && (
          <Card>
            <div className="flex items-center justify-between mb-6">
              <CardTitle>{t.admin.dashboard.recentPosts}</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin/blog">{t.admin.dashboard.viewAll}</Link>
              </Button>
            </div>

            <div className="space-y-4">
              {blogData.posts.slice(0, 5).map((post) => (
                <div
                  key={post.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-neutral-50 dark:bg-neutral-700/50"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-neutral-900 dark:text-white truncate">
                      {post.title}
                    </h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-500">
                      {post.category || t.admin.dashboard.noCategory} &middot;{' '}
                      <span className={post.status === 'published' ? 'text-green-600' : 'text-yellow-600'}>
                        {post.status === 'published' ? t.admin.blog.published : t.admin.blog.draft}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <div className="flex items-center text-neutral-500">
                      <Eye className="w-4 h-4 mr-1" />
                      <span className="text-sm">{post.views_count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
